
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { FilterService } from '@/services/filter.service';
import { FILTER_CONFIG } from '@/constants/filter.constants';

export function useFiltersMenu() {
  const store = useStore();
  const router = useRouter();
  const route = useRoute();

  const expanded = ref(true);
  const selectedValueDrivers = ref([]);
  const selectedPortfolio = ref(null);
  const isMobile = ref(window.innerWidth <= FILTER_CONFIG.MOBILE_BREAKPOINT);

  const layers = computed(() => store.getters.layers);
  const activeLayer = computed(() => store.getters.activeLayer);
  const portfolios = computed(() => store.getters['portfolios/portfolios']);
  const activePortfolio = computed(() => store.getters['portfolios/activePortfolio']);
  const valueDrivers = computed(() => store.getters['valueDrivers/valueDrivers']);
  const getTranslations = computed(() => store.getters['staticContent/getTranslations']);

  const translatedLayers = computed(() =>
    FilterService.processLayerTranslations(layers.value, getTranslations.value)
  );

  const filteredPortfolios = computed(() =>
    FilterService.filterPortfolios(portfolios.value, isMobile.value)
  );

  const menuLabel = computed(() =>
    FilterService.generateMenuLabel(
      activeLayer.value,
      activePortfolio.value,
      getTranslations.value
    )
  );

  const showSolutionsSection = computed(() =>
    FilterService.shouldShowSolutionsSection(expanded.value, activeLayer.value?.id)
  );

  const containerHeight = computed(() =>
    FilterService.getContainerHeight(expanded.value, activeLayer.value?.id)
  );

  const isActiveLayer = (layerId) => layerId === activeLayer.value?.id;

  const toggleMenu = () => {
    expanded.value = !expanded.value;
  };

  const onSelectPortfolio = (portfolio) => {
    store.dispatch('portfolios/setActivePortfolio', { activePortfolio: portfolio });
  };

  const updateValueDrivers = () => {
    store.dispatch('valueDrivers/setActiveValueDrivers', {
      valueDrivers: selectedValueDrivers.value
    });
  };

  const setLayerAndRedirect = (layer) => {
    router.push({
      name: 'LayerScreen',
      params: {
        category: route.params.category,
        sector: route.params.sector,
        layer: layer.id,
      },
    });
  };

  const handleResize = () => {
    isMobile.value = window.innerWidth <= FILTER_CONFIG.MOBILE_BREAKPOINT;
  };

  watch(
    activePortfolio,
    (newPortfolio) => {
      selectedPortfolio.value = newPortfolio;
    },
    { immediate: true }
  );

  watch(
    activeLayer,
    (newLayer) => {
      if (newLayer?.id === FILTER_CONFIG.SOLUTIONS_LAYER_ID && !activePortfolio.value) {
        const defaultPortfolio = { id: 2, slug: 'chemicals', name: 'Chemicals' };
        store.dispatch('portfolios/setActivePortfolio', { activePortfolio: defaultPortfolio });
      }
    },
    { immediate: true }
  );

  onMounted(() => {
    window.addEventListener('resize', handleResize);
    
    if (activeLayer.value?.id === FILTER_CONFIG.SOLUTIONS_LAYER_ID && !activePortfolio.value) {
      const defaultPortfolio = { id: 2, slug: 'chemicals', name: 'Chemicals' };
      store.dispatch('portfolios/setActivePortfolio', { activePortfolio: defaultPortfolio });
    } else if (activePortfolio.value) {
      selectedPortfolio.value = activePortfolio.value;
    }
  });

  onBeforeUnmount(() => {
    window.removeEventListener('resize', handleResize);
  });

  return {
    expanded,
    selectedValueDrivers,
    selectedPortfolio,
    isMobile,
    translatedLayers,
    filteredPortfolios,
    menuLabel,
    showSolutionsSection,
    containerHeight,
    activeLayer,
    activePortfolio,
    valueDrivers,
    getTranslations,
    isActiveLayer,
    toggleMenu,
    onSelectPortfolio,
    updateValueDrivers,
    setLayerAndRedirect,
  };
}